// Copyright ©️ 2026 eVolve MEP, LLC

import { useState, useEffect, useCallback } from 'react';

import type { StrongOmit } from '@evolvemep/api-ts';
import { type TextInputProps, TextInput } from '@mantine/core';

import { convertInchesToImperialString, hasValidTolerance } from 'helpers/convertInchesToImperialString';
import { isNil, isNotNil } from 'helpers/isNotNil';

type BaseInchesInputProps = {
  /** `null` when blank, `NaN` when the typed text isn't a valid measurement. */
  value?: number | null;
  defaultValue?: number | null;
  /**
   * Called as the user types with the measurement, `null` when the input is blank (see `allowBlank`),
   * or `NaN` when the typed text isn't a valid measurement.
   * @remarks `JSON.stringify(NaN)` evaluates as `null`, so a parent that submits this value without validating it
   * will send invalid input to the server as a blank measurement. The parent's validation must reject `NaN`, either
   * directly (`Number.isNaN`) or through a check that already covers it, like a falsy check on a required field.
   */
  onChange: (value: number | null) => void;
  /**
   * If `true`, then `value` is represented as decimal feet, not decimal inches.
   *
   * Some data from Revit is stored in decimal feet,
   * but is only ever displayed/edited in inches.
   *
   * @defaultValue false
   */
  valueInFeet?: boolean;
  /**
   * If `true`, allows negative measurements.
   *
   * @defaultValue false
   */
  allowNegative?: boolean;
  /**
   * If `true`, a blank input is `null`. Otherwise it isn't a valid measurement, and is `NaN`.
   *
   * @defaultValue false
   */
  allowBlank?: boolean;
  /**
   * If provided, blurring the input while it is empty will call `onChange` with this value.
   *
   * In the same unit as `value`.
   *
   * @remarks Ignored if `allowBlank` is `true`.
   */
  blankValue?: number;
} & StrongOmit<TextInputProps, 'value' | 'defaultValue' | 'onChange'>;

type FractionalModeProps = {
  /**
   * The format that the measurement will be displayed in after validation.
   * Allowed values are "fraction" and "decimal".
   *
   * @defaultValue "fraction"
   */
  valueFormat?: 'fraction';
  /**
   * If the `valueFormat` is "fraction", this represents the valid tolerance of the measurement. Must be `1/(power of 2)`.
   *
   * @defaultValue 1/16
   */
  tolerance?: number;
};

type DecimalModeProps = {
  valueFormat: 'decimal';
  /**
   * If the `valueFormat` is "decimal" this represents the number of decimal places to allow.
   *
   * @required when `valueFormat` is "decimal".
   */
  tolerance: number;
};

export type InchesInputProps = BaseInchesInputProps & (FractionalModeProps | DecimalModeProps);

const measurementAsNumberRegex = (decimalPlaces?: number) =>
  new RegExp(`^-?(\\d*(\\.\\d{1,${decimalPlaces ?? ''}})?)$`);
const measurementAsFractionalRegex = /^-?(\d+ )?\d+\/\d+$/;

/**
 * Convert an imperial measurement to a number in inches, or null on failure.
 *
 * @example parseMeasurement("1-1/4", false, "fraction"); // returns 1.25
 *
 * @param value The string value to convert to a number
 * @param allowNegative If true, allows negative measurements
 * @param valueFormat Format to parse the measurement as
 * @param tolerance Allowable fractional tolerance (ie. 1/16), or number of decimal places (ie. 4) when `valueFormat` is "decimal"
 */
export const parseMeasurement = (
  value: string,
  allowNegative: boolean,
  valueFormat: 'fraction' | 'decimal',
  tolerance?: number,
): number | null => {
  const validDenominators: number[] = [];

  // If the value format is a fraction, build the list of valid denominators
  if (valueFormat === 'fraction' && isNotNil(tolerance)) {
    if (!hasValidTolerance(tolerance)) return null;
    for (let i = 1; i <= 1 / tolerance; i *= 2) {
      validDenominators.push(i);
    }
  }

  if (valueFormat === 'decimal' && isNotNil(tolerance)) {
    if (tolerance < 0 || tolerance > 100 || !Number.isInteger(tolerance)) return null;
  }

  // Remove excess "-" markers, spaces, and quotes
  const cleaned = value
    .replaceAll(/(?<!^)-/g, ' ')
    .replaceAll(/ +/g, ' ')
    .trim()
    .replace(/ *"$/, '');

  // Regardless of the final format type, the user can enter a fraction or a decimal.
  // This catches the fraction format.
  if (measurementAsFractionalRegex.test(cleaned)) {
    const [inches = '0', fractional = '0'] = (cleaned.indexOf(' ') < 0 ? `0 ${cleaned}` : cleaned).split(' ');
    const [num = 0, den = 1] = fractional.split('/').map(parseFloat);
    if (validDenominators.length === 0 || validDenominators.includes(den)) {
      const parsed = parseFloat(inches) + num / den;
      return Number.isNaN(parsed) || (!allowNegative && parsed < 0) ? null : parsed;
    }
  }

  // This handles the decimal format.
  if (measurementAsNumberRegex(valueFormat === 'fraction' ? undefined : tolerance).test(cleaned)) {
    const parsed = parseFloat(cleaned);
    if (!Number.isNaN(parsed) && (allowNegative || parsed >= 0)) {
      if (validDenominators.length === 0) return parsed;

      const validDecimals = validDenominators.reduce(
        (out, v) => {
          for (let i = 1; i < v; i++) out.add(i / v);
          return out;
        },
        new Set([0]),
      );

      if (validDecimals.has(Math.abs(parsed) % 1)) {
        return parsed;
      }
    }
  }

  return null;
};

/**
 * Used to allow a user to input inches using either decimals or fractional imperial units.
 * Only allows decimal and fractional for halves, quarters, eighths, sixteenths, and thirty-second inches.
 *
 * Accepts the following format examples:
 *
 * - 1.5
 * - .75
 * - 1 3/4
 * - 3-1/2"
 *
 * Text that isn't a valid measurement is reported to `onChange` as `NaN`. The parent is responsible for
 * rejecting it and passing back an `error` to show.
 */
export const InchesInput = ({
  value: parentValue,
  defaultValue = parentValue,
  onChange: parentOnChange,
  valueInFeet = false,
  valueFormat = 'fraction',
  allowNegative = false,
  allowBlank = false,
  blankValue,
  tolerance = 1 / 16,
  ...props
}: InchesInputProps) => {
  const multiplier = valueInFeet ? 12 : 1;

  const convertToDisplayValue = useCallback(
    (inches: number | null) =>
      inches === null || Number.isNaN(inches)
        ? ''
        : valueFormat === 'fraction'
          ? convertInchesToImperialString(inches, { tolerance })
          : `${Number(inches.toFixed(tolerance))}"`,
    [valueFormat, tolerance],
  );

  /** In inches. `null` for blank text when `allowBlank`, `NaN` for text that isn't a valid measurement. */
  const parseText = useCallback(
    (text: string) =>
      text === '' ? (allowBlank ? null : NaN) : (parseMeasurement(text, allowNegative, valueFormat, tolerance) ?? NaN),
    [allowBlank, allowNegative, valueFormat, tolerance],
  );

  const [text, setText] = useState(() => convertToDisplayValue(isNil(defaultValue) ? null : defaultValue * multiplier));

  useEffect(() => {
    // Skip this effect for uncontrolled inputs (when parentValue is undefined)
    if (parentValue === undefined) return;
    // Not `isNil`, which also matches `NaN`.
    const parentInches = parentValue === null ? null : parentValue * multiplier;
    setText((t) => {
      // Only overwrite `text` when it no longer represents `parentValue`,
      // so the user's in-progress text isn't clobbered while typing.
      const inches = parseText(t);
      if (Object.is(inches, parentInches)) return t;
      // Blank (`null`) and invalid (`NaN`) text both display as '', so they can't be compared as display text.
      if (isNil(inches) || isNil(parentInches)) return convertToDisplayValue(parentInches);
      // Converting from feet is lossy, so valid measurements are compared as display text.
      const parentDisplayValue = convertToDisplayValue(parentInches);
      return convertToDisplayValue(inches) === parentDisplayValue ? t : parentDisplayValue;
    });
  }, [parentValue, multiplier, parseText, convertToDisplayValue]);

  const onChange = (inches: number | null) => parentOnChange(inches === null ? null : inches / multiplier);

  return (
    <TextInput
      placeholder="Inches"
      {...props}
      onBlur={(...e) => {
        if (isNotNil(blankValue) && text === '' && !allowBlank) {
          const inches = blankValue * multiplier;
          setText(convertToDisplayValue(inches));
          onChange(inches);
        } else {
          // Normalize valid text. Invalid text stays as typed so the user can see what to fix.
          const inches = parseText(text);
          if (!Number.isNaN(inches)) setText(convertToDisplayValue(inches));
        }
        props.onBlur?.(...e);
      }}
      value={text}
      onChange={({ currentTarget: { value: newText } }) => {
        setText(newText);
        onChange(parseText(newText));
      }}
    />
  );
};
